import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { visualTokens as V } from '../../../theme_landing_page_2';

const path = 'M -20 60 C 40 60 50 20 125 20 S 200 100 250 100 S 300 20 375 20 S 450 100 500 100 S 550 20 625 20 S 700 100 750 100 S 800 20 875 20 S 970 60 1020 60';
const dotCount = 216;
const flowDuration = 44;
const colours = [V.brandCyan, V.brandBlue, V.brandTeal, V.brandViolet];

const StatsDotFlow: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    let visible = true;
    const updateVisibility = () => setRunning(visible && document.visibilityState !== 'hidden');
    const observer = typeof IntersectionObserver !== 'undefined' ? new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      updateVisibility();
    }) : null;
    const element = ref.current;
    if (element) observer?.observe(element);
    updateVisibility();
    document.addEventListener('visibilitychange', updateVisibility);
    return () => {
      observer?.disconnect();
      document.removeEventListener('visibilitychange', updateVisibility);
    };
  }, []);

  const wave = (
    <svg viewBox="0 0 1000 120" preserveAspectRatio="none" width="100%" height="100%">
      {Array.from({ length: dotCount }, (_, i) => (
        <circle
          key={i}
          r={i % 3 === 0 ? 2.2 : 1.4}
          fill={colours[i % colours.length]}
          opacity={0.18 + (i % 4) * 0.08}
          style={{
            offsetPath: `path("${path}")`,
            offsetDistance: `${i / dotCount * 100}%`,
            offsetRotate: '0deg',
            transform: `translate(${((i * 7) % 11) - 5}px, ${((i * 13) % 19) - 9}px)`,
            animation: `statsDotCurrent ${flowDuration}s linear ${-i / dotCount * flowDuration}s infinite`,
            animationPlayState: running ? 'running' : 'paused',
          }}
        />
      ))}
    </svg>
  );

  return (
    <Box ref={ref} aria-hidden="true" sx={{
      position: 'absolute', inset: 0, pointerEvents: 'none',
      '@keyframes statsDotCurrent': { from: { offsetDistance: '0%' }, to: { offsetDistance: '100%' } },
      '@media (prefers-reduced-motion: reduce)': { '& circle': { animation: 'none !important' } },
    }}>
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: { xs: '50%', sm: '100%' } }}>{wave}</Box>
      <Box sx={{ display: { xs: 'block', sm: 'none' }, position: 'absolute', top: '50%', left: 0, right: 0, height: '50%' }}>{wave}</Box>
    </Box>
  );
};

export default StatsDotFlow;
